import { Alert, Box, Button, CircularProgress, Collapse, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { memo, useCallback, useState } from 'react'
import { IoChevronDown, IoChevronUp } from 'react-icons/io5'

import { useLazyGetQuestionContentQuery } from '@/services/api'
import type { Question } from '@/types'

interface QuestionCardQuestionProps {
  question: Question
}

export const QuestionCardQuestion = memo<QuestionCardQuestionProps>(({ question }) => {
  const theme = useTheme()
  const [expanded, setExpanded] = useState<boolean>(false)
  const [description, setDescription] = useState<string>('')
  const [trigger, { isLoading, error }] = useLazyGetQuestionContentQuery({
    selectFromResult: ({ isLoading, isFetching, error }) => ({
      error,
      isLoading: isLoading || isFetching,
    }),
  })

  const handleToggle = useCallback(async () => {
    if (!expanded && !description && !isLoading) {
      const result = await trigger(question.id).unwrap()

      setDescription(result.description)
    }

    setExpanded((prev) => !prev)
  }, [expanded, description, isLoading, trigger, question.id])

  const getErrorMessage = () => {
    if (error) {
      if ('status' in error) {
        return `Erro ${error.status}: Falha ao carregar descrição`
      }

      return 'Erro ao carregar descrição da questão'
    }

    return ''
  }

  return (
    <Box data-qa="question-card-question" sx={{ mt: 2 }}>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          marginBottom: 2,
          lineHeight: 1.4,
        }}>
        {question.question}
      </Typography>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box
          sx={{
            p: 3,
            mb: 2,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            minHeight: isLoading ? 80 : 'auto',
            display: 'flex',
            alignItems: isLoading ? 'center' : 'stretch',
            justifyContent: isLoading ? 'center' : 'stretch',
          }}>
          {isLoading ? (
            <CircularProgress size={24} />
          ) : error ? (
            <Alert severity="error" variant="outlined" sx={{ width: '100%' }}>
              {getErrorMessage()}
            </Alert>
          ) : description ? (
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ lineHeight: 1.6 }}
              dangerouslySetInnerHTML={{ __html: description }}
            />
          ) : null}
        </Box>
      </Collapse>

      <Button
        onClick={handleToggle}
        variant="text"
        startIcon={expanded ? <IoChevronUp /> : <IoChevronDown />}
        disabled={isLoading}
        sx={{
          textTransform: 'none',
          fontWeight: 500,
        }}>
        {isLoading
          ? 'Carregando...'
          : expanded
            ? 'Mostrar menos'
            : 'Ler descrição completa'}
      </Button>
    </Box>
  )
})

QuestionCardQuestion.displayName = 'QuestionCardQuestion'
